import {
  Tooltip,
  Box,
  Stack,
  Typography,
  IconButton,
  Modal,
  Button,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { HelpOutline, Close } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import DataTable from '../../../../../components/table/datatable';
import useInformationDialog from '../../../../../hooks/useInformationDialog';
import { useLazyGetCreditMemoQuery } from '../../../../../api/front-office/roomposting.api.rtk';
import { RoomPostingCreditMemoVoucherIE } from '../../../interface/room-posting-interface';
import { formatNumber } from '../../../../../utils/format';

const credtitMemoColumns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'CM ID',
    minWidth: 50,
    flex: 1,
    hideable: false,
  },
  {
    field: 'tgl_id',
    headerName: 'Ledger ID',
    minWidth: 100,
    flex: 1,
    hideable: false,
  },
  {
    field: 'guestID',
    headerName: 'Guest ID',
    minWidth: 100,
    flex: 1,
    hideable: false,
  },
  {
    field: 'seriesId',
    headerName: 'Series ID',
    minWidth: 50,
    flex: 1,
    hideable: false,
  },
  {
    field: 'transNo',
    headerName: 'Transaction No.',
    minWidth: 150,
    flex: 1.5,
    hideable: false,
  },

  {
    field: 'creditAmount',
    headerName: 'Credit Amount',
    minWidth: 90,
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'creditBal',
    headerName: 'Credit Available',
    minWidth: 80,
    align: 'right',
    headerAlign: 'right',
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'guestName',
    headerName: 'Guest Name',
    minWidth: 150,
    flex: 1.5,
    hideable: false,
    sortable: false,
  },
  {
    field: 'bookingDetails',
    headerName: 'Reference',
    minWidth: 150,
    flex: 1.5,
    hideable: false,
    sortable: false,
  },
];

const visibleColumns = credtitMemoColumns.filter(
  (column) => !['id', 'tgl_id', 'seriesId', 'guestID'].includes(column.field),
);

const FOCreditMemoTable = ({
  isOpen,
  onClose,
  selectedCM,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedCM: (cMdetails: RoomPostingCreditMemoVoucherIE) => void;
}) => {
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();
  const [cmDetails, setCMDetails] = useState<RoomPostingCreditMemoVoucherIE[]>(
    [],
  );

  const [selCM, setSelCM] = useState<RoomPostingCreditMemoVoucherIE>();
  const [CMData, { isLoading }] = useLazyGetCreditMemoQuery();

  useEffect(() => {
    if (isOpen) {
      getCMListFunction();
    }
  }, [CMData, isOpen]);

  const getCMListFunction = async () => {
    try {
      const response = await CMData({
        cmID: '0',
        voided: 'No',
      }).unwrap();

      setCMDetails(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch credit memo list:', error);
    }
  };

  const handleSelect = () => {
    if (!selCM) {
      onShowInfoDialog({
        title: 'No credit memo',
        desc: 'Please select Credit Memo to continue.',
      });
      return;
    }

    selectedCM(selCM);
    onClose();
  };
  return (
    <Modal
      hideBackdrop={true}
      open={isOpen}
      onClose={onClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          p: '24px',
          position: 'fixed',
          bottom: 'center',
          right: 'center',
          transform: 'translate(0%, 0%)',
          width: {
            xs: '400px',
            sm: '650px',
            md: '820px',
            lg: '900px',
            xl: '900px',
          },
          height: '580px',
          borderRadius: '5px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          overflow: 'auto',
        }}
      >
        <form id='pre-approve-city-ledger'>
          <Stack
            direction='row'
            justifyContent='space-between'
            borderBottom='1px solid #EEE'
            p='16px 0'
          >
            <Typography
              variant='h6'
              component='div'
              sx={{ flexGrow: 1, color: '#000000' }}
            >
              Select Credit Memo
              <Tooltip title='Learn More' placement='right-end'>
                <IconButton
                  sx={{
                    border: 0,
                    color: 'textGray',
                    bgcolor: 'transparent',
                    ':hover': {
                      color: 'primary',
                      bgcolor: 'transparent',
                    },
                  }}
                  disableRipple
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Typography>
            <IconButton color='inherit' onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
          <Stack direction='column' gap={1}>
            <Stack direction='row'>
              <DataTable
                getRowId={(row) => row.id}
                columns={visibleColumns}
                //rows={cmDetails || []}
                rows={(cmDetails || [])
                  .filter((row) => row.id)
                  .filter((row) => {
                    return row.creditBal > 0;
                  })
                  .map((row) => {
                    const fCreditAmount = formatNumber(row.creditAmount, 2, 2);
                    const fcreditBal = formatNumber(row.creditBal, 2, 2);

                    return {
                      ...row,
                      creditAmount: fCreditAmount,
                      creditBal: fcreditBal,
                    };
                  })}
                checkboxSelection={false}
                onRowSelectionModelChange={(newSelection) => {
                  const selectedRow = cmDetails.find(
                    (row) => row.id === newSelection[0],
                  );

                  setSelCM(selectedRow);
                }}
                loading={isLoading}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                pageSize={100}
                autoPageSize={false}
                tableStyle={{
                  padding: 0,
                  '&.MuiDataGrid-root': {
                    p: 0,
                  },
                  '& .MuiTablePagination-root': {
                    p: '0 10px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'offWhite',
                    borderRadius: 0,
                  },
                  ".MuiButtonBase-root[aria-label='Export']": {
                    display: 'none!important',
                  },
                }}
                height={400}
              />
            </Stack>
            <Box mb={1} />
            <Stack
              direction='row'
              justifyContent='flex-end'
              borderBottom='1px solid #EEE'
            ></Stack>
          </Stack>
          <Box mb={1} />
          <Stack direction='row' gap={1} justifyContent='flex-end'>
            <Button
              sx={{
                width: '100px',
                height: '35px',
                border: '1px solid',
                backgroundColor: 'transparent',
              }}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant='contained'
              sx={{
                width: 'auto',
                height: '35px',
              }}
              onClick={handleSelect}
            >
              Select
            </Button>
          </Stack>
        </form>

        <DialogInformation />
      </Box>
    </Modal>
  );
};

export default memo(FOCreditMemoTable);
